import type { ReactNode } from "react"
import Image from "next/image"
import { blobMediaUrl } from "@/lib/blob-url"
import { ArrowRightIcon } from "@/components/ui/button"
import { PlaceholderMedia } from "@/components/ui/placeholder-media"

export interface QuestionCard {
  quote: string
  /** Plain text, or a rich node (e.g. an answer with an inline source link). */
  explanation: ReactNode
  photo: string
  biomarkerName: string
  biomarkerValue: string
  biomarkerStatus: "normal" | "observation" | "negative"
  /** Optional, locale-prefixed href to a related insights article. */
  articleHref?: string
  /** Optional CSS object-position for the circular thumbnail. */
  photoObjectPosition?: string
}

interface QuestionsHelpSectionProps {
  heading: string
  questions: QuestionCard[]
  label?: string
  description?: string
  /** Label for the per-card "Learn more" link (shown only when a card has a
   *  related article). Pass the translated `ui.learnMore` string. */
  learnMoreLabel?: string
  /**
   * `default` — the original shared layout (avatar left, quoted question,
   * 3-line clamp) used by every test page.
   * `feature` — the full-body-MRI treatment (photo pinned top-right, no
   * quotes, bottom-aligned untruncated answer + "Learn more" link).
   */
  variant?: "default" | "feature"
}

export function QuestionsHelpSection({
  heading,
  questions,
  label,
  description,
  learnMoreLabel,
  variant = "default",
}: QuestionsHelpSectionProps) {
  if (questions.length === 0) return null

  const isFeature = variant === "feature"

  return (
    <section
      id="questions"
      className="overflow-x-clip bg-neutral-50 py-16 md:py-20"
    >
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-20">
          {/* Left rail */}
          <div>
            {label && (
              <span
                className={
                  isFeature
                    ? "type-label-lg text-neutral-500 uppercase"
                    : "type-label-md text-deep-green-700 tracking-wider uppercase"
                }
              >
                {label}
              </span>
            )}
            <h2
              className={`mt-3 text-neutral-900 ${
                isFeature ? "type-display-md" : "type-display-lg"
              }`}
            >
              {heading}
            </h2>
            {description && (
              <p className="type-body-lg mt-5 max-w-[440px] text-neutral-600">
                {description}
              </p>
            )}
          </div>

          {/* Right — 2x2 card grid */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {questions.map((q, i) =>
              isFeature ? (
                <article
                  key={i}
                  className="flex h-full flex-col gap-4 rounded-[16px] bg-white p-8"
                >
                  {/* Question on the left, profile photo pinned top-right */}
                  <div className="flex items-start justify-between gap-4">
                    <p className="type-title-lg text-neutral-900">{q.quote}</p>
                    <div className="relative size-16 shrink-0 overflow-hidden rounded-md bg-neutral-200">
                      {q.photo ? (
                        <Image
                          src={blobMediaUrl(q.photo)}
                          alt=""
                          fill
                          sizes="64px"
                          className="object-cover"
                          style={
                            q.photoObjectPosition
                              ? { objectPosition: q.photoObjectPosition }
                              : undefined
                          }
                        />
                      ) : (
                        <PlaceholderMedia label="" className="h-full w-full" />
                      )}
                    </div>
                  </div>
                  {/* Description (+ optional link) pinned to the card bottom */}
                  <div className="mt-auto flex flex-col gap-4 pt-4">
                    <p className="type-body-md text-neutral-600">
                      {q.explanation}
                    </p>
                    {q.articleHref && (
                      <a
                        href={q.articleHref}
                        className="type-body-md text-aubergine-800 inline-flex items-center gap-1 font-medium underline-offset-4 hover:underline"
                      >
                        {learnMoreLabel ?? "Learn more"}
                        <ArrowRightIcon size={14} />
                      </a>
                    )}
                  </div>
                </article>
              ) : (
                <article
                  key={i}
                  className="flex gap-4 rounded-[16px] bg-white p-6"
                >
                  <div className="relative size-12 shrink-0 overflow-hidden rounded-full bg-neutral-200">
                    {q.photo ? (
                      <Image
                        src={blobMediaUrl(q.photo)}
                        alt=""
                        fill
                        sizes="48px"
                        className="object-cover"
                      />
                    ) : (
                      <PlaceholderMedia label="" className="h-full w-full" />
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <blockquote className="type-title-lg text-neutral-900">
                      <span className="text-neutral-400">&ldquo;</span>
                      {q.quote}
                      <span className="text-neutral-400">&rdquo;</span>
                    </blockquote>
                    <p className="type-body-md line-clamp-3 text-neutral-600">
                      {q.explanation}
                    </p>
                  </div>
                </article>
              )
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
